import React, { useState, useEffect, useMemo } from 'react';
import {
  LifeBuoy, RefreshCw, Search, X, Plus, Loader2, Send, MessageSquare,
} from 'lucide-react';
import { supportAPI } from '../../services/supportApi';
import {
  TICKET_STATUSES, TICKET_TYPES, TICKET_PRIORITIES,
  TypeBadge, PriorityBadge, StatusBadge, TicketFormModal, dtfmt,
} from './supportCommon';

/**
 * Support «Αιτήματα»: όλα τα tickets (παράπονα, αιτήματα, βλάβες, ερωτήσεις)
 * με φίλτρα, δημιουργία και νήμα σχολίων ανά ticket.
 */

const SupportTicketsTab: React.FC = () => {
  const [tickets, setTickets] = useState<any[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('active'); // active | open | in_progress | resolved | all
  const [typeFilter, setTypeFilter] = useState('');
  const [storeFilter, setStoreFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selId, setSelId] = useState<number | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (statusFilter !== 'all' && statusFilter !== 'active') params.status = statusFilter;
      if (typeFilter) params.type = typeFilter;
      if (storeFilter) params.store_id = parseInt(storeFilter);
      if (search.trim()) params.q = search.trim();
      const d: any = await supportAPI.getTickets(params);
      let rows = d.tickets || [];
      if (statusFilter === 'active') rows = rows.filter((t: any) => t.status !== 'resolved');
      setTickets(rows);
    } catch (e) {
      console.error('Tickets load failed:', e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [statusFilter, typeFilter, storeFilter]);
  useEffect(() => {
    supportAPI.getStores().then((d: any) => setStores(d.stores || [])).catch(() => {});
  }, []);

  const counters = useMemo(() => {
    let open = 0, inProgress = 0;
    for (const t of tickets) {
      if (t.status === 'open') open++;
      else if (t.status === 'in_progress') inProgress++;
    }
    return { open, inProgress };
  }, [tickets]);

  const statusChips = [
    { value: 'active', label: 'Ενεργά' },
    { value: 'open', label: 'Ανοιχτά' },
    { value: 'in_progress', label: 'Σε εξέλιξη' },
    { value: 'resolved', label: 'Επιλυμένα' },
    { value: 'all', label: 'Όλα' },
  ];

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-t1 flex items-center gap-2">
            <LifeBuoy className="h-6 w-6 text-cyan-600 dark:text-cyan-400" />Αιτήματα & Παράπονα
          </h1>
          <p className="text-sm text-t3">
            {counters.open} ανοιχτά · {counters.inProgress} σε εξέλιξη
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg text-sm font-medium"
        >
          <Plus className="h-4 w-4" /> Νέο ticket
        </button>
      </div>

      {/* Φίλτρα */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 bg-surface-2 dark:bg-surface-1 border border-line rounded-lg p-1">
          {statusChips.map(c => (
            <button
              key={c.value}
              onClick={() => setStatusFilter(c.value)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium ${
                statusFilter === c.value ? 'bg-cyan-500/20 text-cyan-700 dark:text-cyan-300' : 'text-t3 hover:text-t1'
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
        <select
          value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
          className="bg-surface-2 dark:bg-surface-1 border border-line text-t2 rounded-lg px-3 py-2 text-sm"
        >
          <option value="">Όλοι οι τύποι</option>
          {TICKET_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select
          value={storeFilter} onChange={(e) => setStoreFilter(e.target.value)}
          className="bg-surface-2 dark:bg-surface-1 border border-line text-t2 rounded-lg px-3 py-2 text-sm max-w-[220px]"
        >
          <option value="">Όλα τα καταστήματα</option>
          {stores.map((s: any) => <option key={s.id} value={s.id}>{s.display_name || s.name} (#{s.id})</option>)}
        </select>
        <div className="relative">
          <Search className="h-4 w-4 text-t4 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
            placeholder="Αναζήτηση… (Enter)"
            className="w-56 bg-surface-2 dark:bg-surface-1 border border-line text-t1 rounded-lg pl-9 pr-3 py-2 text-sm placeholder:text-t4"
          />
        </div>
        <button onClick={load} className="p-2 text-t3 hover:text-t1 bg-surface-2 dark:bg-surface-1 border border-line rounded-lg" title="Ανανέωση">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {/* Λίστα */}
      {loading ? (
        <div className="flex items-center justify-center py-24"><RefreshCw className="h-6 w-6 animate-spin text-cyan-600 dark:text-cyan-400" /></div>
      ) : tickets.length > 0 ? (
        <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line divide-y divide-line/60 overflow-hidden">
          {tickets.map(t => (
            <button
              key={t.id} onClick={() => setSelId(t.id)}
              className="w-full text-left px-4 py-3 hover:bg-surface-3 dark:hover:bg-surface-2/30 transition-colors"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-t1 truncate">{t.title}</p>
                  <p className="text-xs text-t4">
                    {t.store_display_name || t.store_name} (#{t.store_id}) · {dtfmt(t.created_at)}
                    {t.opened_by_name ? ` · ${t.opened_by_name}` : ''}
                    {t.comment_count > 0 && (
                      <span className="inline-flex items-center gap-0.5 ml-1.5 text-t3">
                        <MessageSquare className="h-3 w-3" />{t.comment_count}
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <TypeBadge type={t.type} />
                  <PriorityBadge priority={t.priority} />
                  <StatusBadge status={t.status} />
                </div>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <p className="text-sm text-t4 text-center py-16">Κανένα ticket με αυτά τα φίλτρα.</p>
      )}

      {showCreate && (
        <TicketFormModal stores={stores} onClose={() => setShowCreate(false)} onCreated={load} />
      )}
      {selId !== null && (
        <TicketDetailModal ticketId={selId} onClose={() => setSelId(null)} onChanged={load} />
      )}
    </div>
  );
};

// ── Λεπτομέρεια ticket με νήμα σχολίων ───────────────────────────────────────
const TicketDetailModal: React.FC<{ ticketId: number; onClose: () => void; onChanged: () => void }> = ({ ticketId, onClose, onChanged }) => {
  const [ticket, setTicket] = useState<any | null>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newComment, setNewComment] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const d: any = await supportAPI.getTicket(ticketId);
      setTicket(d.ticket);
      setComments(d.comments || []);
    } catch (e) {
      console.error('Ticket detail failed:', e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ticketId]);

  const patch = async (data: any) => {
    try {
      setBusy(true);
      const d: any = await supportAPI.updateTicket(ticketId, data);
      // Το PUT επιστρέφει την ωμή γραμμή (χωρίς τα joined store_name/opened_by_name)
      // — merge ώστε να μη χαθούν τα ονόματα που ήρθαν από το αρχικό GET.
      setTicket((prev: any) => ({ ...prev, ...d.ticket }));
      onChanged();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Αποτυχία ενημέρωσης');
    } finally {
      setBusy(false);
    }
  };

  const addComment = async () => {
    const body = newComment.trim();
    if (!body) return;
    try {
      setBusy(true);
      await supportAPI.addTicketComment(ticketId, body);
      setNewComment('');
      await load();
      onChanged();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Αποτυχία σχολίου');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line w-full max-w-2xl shadow-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-line shrink-0">
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-t1 truncate">{ticket?.title || `Ticket #${ticketId}`}</h3>
            {ticket && (
              <p className="text-xs text-t3">
                {ticket.store_display_name || ticket.store_name} (#{ticket.store_id}) · {dtfmt(ticket.created_at)}
                {ticket.opened_by_name ? ` · ${ticket.opened_by_name}` : ''}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-1 text-t3 hover:text-t1"><X className="h-5 w-5" /></button>
        </div>

        {loading || !ticket ? (
          <div className="flex items-center justify-center py-24"><RefreshCw className="h-6 w-6 animate-spin text-cyan-600 dark:text-cyan-400" /></div>
        ) : (
          <div className="p-6 space-y-5 overflow-y-auto">
            {/* Κατάσταση / τύπος / προτεραιότητα */}
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={ticket.status} disabled={busy}
                onChange={(e) => patch({ status: e.target.value })}
                className="bg-surface-2 border border-line-strong text-t1 rounded-lg px-3 py-1.5 text-sm"
              >
                {TICKET_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <select
                value={ticket.type} disabled={busy}
                onChange={(e) => patch({ type: e.target.value })}
                className="bg-surface-2 border border-line-strong text-t1 rounded-lg px-3 py-1.5 text-sm"
              >
                {TICKET_TYPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <select
                value={ticket.priority} disabled={busy}
                onChange={(e) => patch({ priority: e.target.value })}
                className="bg-surface-2 border border-line-strong text-t1 rounded-lg px-3 py-1.5 text-sm"
              >
                {TICKET_PRIORITIES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              {ticket.resolved_at && (
                <span className="text-xs text-emerald-600 dark:text-emerald-400">Επιλύθηκε {dtfmt(ticket.resolved_at)}</span>
              )}
            </div>

            {ticket.description && (
              <div className="bg-surface-2 dark:bg-surface-2/40 border border-line-strong rounded-xl p-4 text-sm text-t2 whitespace-pre-wrap">
                {ticket.description}
              </div>
            )}

            {/* Σχόλια */}
            <div>
              <h4 className="text-sm font-semibold text-t1 mb-2">Σχόλια</h4>
              {comments.length > 0 ? (
                <div className="space-y-2">
                  {comments.map(c => (
                    <div key={c.id} className="bg-surface-2 dark:bg-surface-2/30 border border-line rounded-lg px-3 py-2">
                      <p className="text-sm text-t2 whitespace-pre-wrap">{c.body}</p>
                      <p className="text-xs text-t4 mt-1">{c.author_name || '—'} · {dtfmt(c.created_at)}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-t4">Κανένα σχόλιο ακόμα.</p>
              )}
              <div className="flex items-end gap-2 mt-3">
                <textarea
                  value={newComment} rows={2}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Νέο σχόλιο — τι έγινε, τι απαντήθηκε…"
                  className="flex-1 bg-surface-2 border border-line-strong text-t1 rounded-lg px-3 py-2 text-sm placeholder:text-t4 resize-y"
                />
                <button
                  onClick={addComment} disabled={busy || !newComment.trim()}
                  className="p-2.5 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white rounded-lg"
                  title="Αποστολή σχολίου"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportTicketsTab;
